import { useQuery } from "@apollo/client/react";
import { GET_DEALS_LIST } from "@/hooks";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import type { QueryGetAllDealsArgs } from "@/types/graphql";

type DealsListFilters = Partial<QueryGetAllDealsArgs>;

type DealsListValue = string | null | undefined;

type DealsListDefaults = {
  searchString: DealsListValue;
  customer: DealsListValue;
  subsidiary: DealsListValue;
  building: DealsListValue;
  meter: DealsListValue;
  energyType: DealsListValue;
  fromDate: DealsListValue;
  toDate: DealsListValue;
};

/**
 * Custom hook to fetch the list of deals.
 * @param {object} props - The properties to be passed to the query.
 * @returns {{dealsList: Array<object>, loading: boolean}} - The list of deals and the loading state.
 */
export function useDealsList(props: DealsListFilters = {}) {
  const propsDefault: DealsListDefaults = {
    searchString: null,
    customer: null,
    subsidiary: null,
    building: null,
    meter: null,
    energyType: null,
    fromDate: null,
    toDate: null,
  };

  // Merge props with defaults
  const mergedVariables = {
    ...propsDefault,
    ...props,
  };

  // Filter out null, undefined, and empty string values to prevent "null" strings being sent
  const variables: Partial<QueryGetAllDealsArgs> = {};

  Object.entries(mergedVariables).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      variables[key as keyof QueryGetAllDealsArgs] = value as string;
    }
  });

  const { data, loading, error } = useQuery<any>(GET_DEALS_LIST, {
    variables,
    errorPolicy: "all",
  });

  // 🔹 Handle errors via useEffect
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    }
  }, [error]);

  return {
    dealsList: data?.getAllDeals || [],
    loading,
  };
}
